import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from "@/shared/hooks/api/useWorkspace";
import { AgentBase } from "@/features/Agents/types/AgentBaseInterface";
import { AgentInstallerSettings, AgentWithIntegrations } from "@/features/Agents/types/AgentInstaller";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Input } from "@/shared/components/ui/input";
import { Separator } from "@/shared/components/ui/separator";
import { Plus, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/shared/components/ui/dialog";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useAgentBase } from "@/features/Agents/hooks/useAgentBase";
import { AgentInstallerWizard } from "@/features/Agents/components/AgentInstaller/AgentInstallerWizard";

interface PipelineSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPipelineInstalled?: () => Promise<void>;
}

export function PipelineSelectorModal({
  isOpen,
  onClose,
  onPipelineInstalled
}: PipelineSelectorModalProps) {
  const { t } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const [selectedPipeline, setSelectedPipeline] = useState<AgentWithIntegrations | null>(null);
  const [isInstallerOpen, setIsInstallerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: pipelineData,
    isLoading: isLoadingPipelines,
    isError: isPipelinesError,
    error: pipelinesError
  } = useAgentBase(
    {
      workspaceId: currentWorkspace?.id || "",
      type: 1, // Filtrando apenas pipelines
      includeAvailable: true, // Incluir agentes disponíveis para instalação
      pageSize: 1000 // Buscar todos os pipelines disponíveis
    },
    isOpen // Só buscar quando o modal estiver aberto
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const availablePipelines = pipelineData?.data?.filter(pipeline => pipeline.status === 'available') || [];

  const normalizeIntegrationMeta = (pipeline: AgentBase) => {
    if (pipeline.linkedIntegrations?.length) {
      return pipeline.linkedIntegrations;
    }

    const settingsIntegrations = Array.isArray(pipeline.settings?.integrations)
      ? pipeline.settings.integrations
      : [];

    if (settingsIntegrations.length > 0) {
      return settingsIntegrations;
    }

    const workflowSettings = pipeline.settings?.workflow_settings as Record<string, unknown> | undefined;
    const nodes = (workflowSettings?.nodes as WorkflowNode[] | undefined)
      || (pipeline.settings?.nodes as WorkflowNode[] | undefined)
      || [];

    interface WorkflowNode {
      config?: {
        integration?: {
          name?: string;
          logo_url?: string;
        };
      };
    }

    const collected = new Map<string, { name: string; logo_url?: string }>();
    nodes.forEach((node) => {
      const integrationName = node?.config?.integration?.name;
      if (integrationName && !collected.has(integrationName)) {
        collected.set(integrationName, {
          name: integrationName,
          logo_url: node?.config?.integration?.logo_url
        });
      }
    });

    return Array.from(collected.values());
  };

  const filteredPipelines = useMemo(() => {
    if (!searchTerm) {
      return availablePipelines;
    }

    const term = searchTerm.toLowerCase();
    return availablePipelines.filter((pipeline) => {
      const integrations = normalizeIntegrationMeta(pipeline);
      const integrationText = integrations.map((item) => item.name).join(" ");
      return [pipeline.name, pipeline.description, integrationText]
        .filter(Boolean)
        .some((text) => text!.toLowerCase().includes(term));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePipelines, searchTerm]);

  const renderPipelineCard = (pipeline: AgentBase) => {
    const installerDescription = (pipeline.settings?.description as string | undefined) || (pipeline.settings?.installer_description as string | undefined);

    return (
      <Card
        key={pipeline.id}
        className="group border-border/60 bg-card/80 backdrop-blur-sm transition-all hover:border-primary/40 hover:shadow-lg"
      >
        <CardHeader className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Badge variant="outline" className="mb-2 text-xs uppercase tracking-wide">
                {t('knowledge.pipelines.pipeline_label')}
              </Badge>
              <CardTitle className="text-xl font-semibold">
                {pipeline.name}
              </CardTitle>
              <CardDescription className="mt-2 text-sm text-muted-foreground">
                {pipeline.description || installerDescription || t('knowledge.pipelines.no_description')}
              </CardDescription>
            </div>
            {pipeline.logo_url && (
              <div className="relative">
                <div className="absolute inset-0 rounded-xl bg-primary/20 blur-lg" />
                <img
                  src={pipeline.logo_url}
                  alt={pipeline.name}
                  className="relative h-14 w-14 rounded-xl border object-cover"
                />
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Separator className="bg-border/60" />

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-muted-foreground">
              {(pipeline.settings?.installer as { steps?: unknown[] } | undefined)?.steps?.length
                ? t('knowledge.pipelines.steps_count', { count: (pipeline.settings?.installer as { steps?: unknown[] } | undefined)?.steps?.length })
                : t('knowledge.pipelines.quick_install')}
            </div>
            <Button onClick={() => handlePipelineSelect(pipeline)} className="w-full md:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              {t('knowledge.pipelines.install_pipeline')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const normalizeAgentForInstaller = (pipeline: AgentBase): AgentWithIntegrations => {
    return {
      ...pipeline,
      avatar_url: pipeline.avatar_url ?? undefined,
      settings: pipeline.settings as unknown as AgentInstallerSettings,
      linkedIntegrations: (pipeline.linkedIntegrations || []).map(li => ({
        id: (li as { id?: string }).id || li.name,
        name: li.name,
        logo_url: li.logo_url || null
      }))
    };
  };

  const handlePipelineSelect = (pipeline: AgentBase) => {
    setSelectedPipeline(normalizeAgentForInstaller(pipeline));
    setIsInstallerOpen(true);
  };

  const handleInstallComplete = async () => {
    setIsInstallerOpen(false);
    setSelectedPipeline(null);
    onClose();
    if (onPipelineInstalled) {
      await onPipelineInstalled();
    }
  };

  const handleCloseInstaller = () => {
    setIsInstallerOpen(false);
    setSelectedPipeline(null);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-2xl font-semibold tracking-tight">
              {t('knowledge.pipelines.addPipelineTitle')}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground">
              {t('knowledge.pipelines.addPipelineDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-2xl border bg-muted/30 p-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('knowledge.pipelines.available_templates', { count: availablePipelines.length })}
                </p>
                <p className="text-sm text-muted-foreground/80">
                  {t('knowledge.pipelines.available_templates_hint')}
                </p>
              </div>
              <div className="relative w-full md:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t('knowledge.pipelines.search_placeholder')}
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          <ScrollArea className="mt-5 max-h-[50vh] pr-4">
            {isLoadingPipelines ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Card key={index} className="space-y-4">
                    <CardHeader>
                      <Skeleton className="h-6 w-2/3" />
                      <Skeleton className="mt-2 h-4 w-full" />
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-10 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : isPipelinesError ? (
              <div className="py-10 text-center text-sm text-red-500">
                {pipelinesError instanceof Error
                  ? pipelinesError.message
                  : t('knowledge.pipelines.error_loading')}
              </div>
            ) : filteredPipelines.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                {searchTerm
                  ? t('knowledge.pipelines.no_results')
                  : t('knowledge.pipelines.no_available_pipelines')}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {filteredPipelines.map(renderPipelineCard)}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Pipeline Installer Wizard */}
      {selectedPipeline && (
        <AgentInstallerWizard
          agent={selectedPipeline}
          isOpen={isInstallerOpen}
          onClose={handleCloseInstaller}
          onInstallComplete={handleInstallComplete}
          onRefreshAgentList={onPipelineInstalled}
        />
      )}
    </>
  );
}
